import { Permission } from "@core/enums/Permission";
import { Role, Roles } from "@core/enums/Role";
import { Menu } from "@core/interface/menu";
import { SubMenu } from "@core/interface/sub-menu";
import { configuration } from "properties/config";

// ============================================
// SHARED/COMMON CONSTANTS (Foundation Layer)
// ============================================

/**
 * Common labels used across the application
 * These are reusable strings that appear in multiple modules
 */
export const COMMON_LABELS = {
  // Status/Feedback
  SUCCESS: "Success",
  ERROR: "Error",
  WARNING: "Warning",
  INFO: "Information",

  // Actions - Primary
  OK: "OK",
  CANCEL: "Cancel",
  CLOSE: "Close",
  SAVE: "Save",
  SUBMIT: "Submit",
  CONTINUE: "Continue",
  DONE: "Done",
  BACK: "Back",

  // Actions - Secondary
  YES: "Yes",
  NO: "No",
  EDIT: "Edit",
  DELETE: "Delete",
  VIEW: "View",
  ADD: "Add",
  REMOVE: "Remove",
  SEARCH: "Search",
  EXPORT: "Export",

  // Common Placeholders
  SELECT: "Select",
  ENTER_TEXT: "Enter text...",
  SEARCH_PLACEHOLDER: "Search...",
  FILTER_SEARCH_PLACEHOLDER: "ui.common.actions.SEARCH",
} as const;

// ============================================
// DOMAIN-SPECIFIC CONSTANTS
// ============================================

// Regional Contact Information for Support
//TBD : should come from backend in future
export const REGIONAL_CONTACT_INFO = [
  { region: "APAC", email: "APSTAC@BAYER.COM" },
  { region: "EMEA", email: "EUTAC@BAYER.COM", chat: "EUTAC.Net" },
  { region: "USA/Canada", email: "TAC@BAYER.COM", phone: "1-800-633-7237" },
  {
    region: "LATAM",
    countries: [
      {
        country: "Brazil",
        email: "TAC_BRASIL@BAYER.COM",
        phone: undefined,
        chat: undefined,
      },
      {
        country: "CONOSUR",
        email: "ST_RAD_CONOSUR@BAYER.COM",
        phone: undefined,
        chat: undefined,
      },
      {
        country: "Mexico",
        email: "SERVICIO.COMODATO@BAYER.COM",
        phone: undefined,
        chat: undefined,
      },
      {
        country: "PACA",
        email: "SERVICE-PACA-RAD@BAYER.COM",
        phone: undefined,
        chat: undefined,
      },
    ],
  },
];

// User Status Constants
export const USER_STATUS = {
  ACTIVE: "Active",
  INACTIVE: "Inactive",
} as const;

export const USER_STATUS_OPTIONS = [
  { label: USER_STATUS.ACTIVE, value: "active" },
  { label: USER_STATUS.INACTIVE, value: "inactive" },
] as const;

const EMAIL_ADDRESS = {
  field: "email_address",
  header: "Email ID",
  i18nKey: "ui.common.fields.EMAIL_ID",
  sortable: false,
};
const ROLE_TYPE = {
  field: "role_type",
  header: "Role Type",
  i18nKey: "ui.common.fields.ROLE_TYPE",
  sortable: false,
};
const TIMESTAMP = {
  field: "timestamp",
  header: "Timestamp",
  i18nKey: "ui.common.fields.TIMESTAMP",
  sortable: false,
  width: "10rem",
};
const ACTION = {
  field: "action",
  header: "Action",
  i18nKey: "ui.common.fields.ACTION",
  sortable: false,
  width: "4rem",
};

const GLOBAN_NUMBER = {
  field: "globan_number",
  header: "Globan Number",
  sortable: false,
  width: "4rem",
};

const ENTITY = {
  field: "globan_number",
  header: "Entity",
  sortable: false,
  width: "4rem",
};

const REMARKS = {
  field: "remarks",
  header: "Remarks",
  i18nKey: "ui.common.fields.REMARKS",
  sortable: false,
  width: "4rem",
};

const PRODUCT_LINE = {
  field: "product_line",
  header: "Product Line",
  i18nKey: "ui.common.fields.PRODUCT_LINE",
  sortable: false,
  isMultiValue: true,
};
const PRODUCT_SOFTWARE_VERSION = {
  field: "product_software_version",
  header: "Product Software Version",
  i18nKey: "ui.common.fields.PRODUCT_SOFTWARE_VERSION",
  sortable: false,
  isMultiValue: true,
};
const ADD_ON_FEATURE = {
  field: "injector_feature_add_ons",
  header: "Add-on Feature",
  i18nKey: "ui.common.fields.FEATURE_ADDON",
  sortable: false,
  isMultiValue: true,
};
const NEW_USER_NAME = {
  field: "new_user_name",
  header: "New User Name",
  i18nKey: "ui.audit_log.column.NEW_USER_NAME",
  sortable: false,
};
const NEW_USER_ROLE = {
  field: "new_user_role",
  header: "New User Role",
  i18nKey: "ui.audit_log.column.NEW_USER_ROLE",
  sortable: false,
};
const NEW_USER_ASSIGNED_COUNTRIES = {
  field: "new_user_assigned_countries",
  header: "Assigned Countries",
  i18nKey: "ui.common.fields.ASSIGNED_COUNTRIES",
  sortable: false,
  isMultiValue: true,
};
const NEW_USER_ASSIGNED_REGIONS = {
  field: "new_user_assigned_regions",
  header: "Assigned Region",
  i18nKey: "ui.common.fields.ASSIGNED_REGION",
  sortable: false,
  isMultiValue: true,
};
const CUSTOMER_NAME = {
  field: "customer_name",
  header: "Customer Name",
  i18nKey: "ui.common.fields.CUSTOMER_NAME",
  sortable: false,
};
const DETAILS = {
  field: "details",
  header: "Details",
  i18nKey: "ui.audit_log.column.DETAILS",
  sortable: false,
};
const INJECTOR_PLACEMENT_SITE = {
  field: "customer_name",
  header: "Customer Name",
  i18nKey: "ui.common.fields.CUSTOMER_NAME",
  sortable: false,
};
const ACTIVITY_DETAILS = {
  field: "details",
  header: "Activity Details",
  i18nKey: "ui.audit_log.column.ACTIVITY_DET",
  sortable: false,
};
const STATUS = {
  field: "status",
  header: "Status",
  i18nKey: "ui.common.fields.STATUS",
  sortable: false,
  width: "6rem",
};
const USERNAME = {
  field: "user_name",
  header: "User Name",
  i18nKey: "ui.common.fields.USER_NAME",
  sortable: false,
};
const CUSTOMER_LOCATION = {
  field: "customer_location",
  header: "Customer Location",
  sortable: false,
};
const PRODUCT_ADDED_EDITED = {
  field: "product_added_edited",
  header: "Product Added/Edited",
  sortable: false,
};
const SOFTWARE_PACKAGE = {
  field: "software_package",
  header: "Software Package",
  sortable: false,
  isMultiValue: true,
};
const DATA_SENT_OR_RECEIVED = {
  field: "data_sent_or_received",
  header: "Data Sent/Received",
  sortable: false,
};
const REQUEST_ID = {
  field: "request_id",
  header: "Request No.",
  i18nKey: "ui.audit_log.column.REQUEST_NO",
  sortable: false,
  width: "18rem",
};
const FILE_NAME = {
  field: "file_name",
  header: "File Name ",
  i18nKey: "ui.audit_log.column.FILE_NAME",
  sortable: false,
  width: "10rem",
};
const FILE_SIZE = {
  field: "file_size",
  header: "File Size",
  i18nKey: "ui.audit_log.column.FILE_SIZE",
  sortable: false,
  width: "10rem",
};
const UPDATED_AT = {
  field: "updated_at",
  header: "Updated At",
  i18nKey: "ui.common.fields.UPDATED_AT",
  sortable: false,
  maxWidth: "6rem",
};

const CREATED_AT = {
  field: "created_at",
  header: "Created At",
  sortable: false,
  maxWidth: "6rem",
};
const NO_OF_INJECTORS = {
  field: "no_of_injectors",
  header: "No. of Injectors",
  i18nKey: "ui.customer_onboarding.NUMBER_OF_INJECTORS",
  sortable: false,
  width: "10rem",
};

//TBD : should come from backend in future
export const ASSIGNED_REGIONS = [
  {
    id: 2,
    region_name: "Americas",
    backend_label: "US",
  },
  {
    id: 3,
    region_name: "Asia Pacific",
    backend_label: "APAC",
  },
  {
    id: 5,
    region_name: "EMEA",
    backend_label: "EMEA",
  },
  {
    id: 1,
    region_name: "Latin America",
    backend_label: "LATAM",
  },
];

const USER_ROLE_FILTER = {
  key: "userRole",
  label: "User Role",
  i18nKey: "ui.common.fields.USER_ROLE",
  type: "dropdown",
  options: [],
};

const STATUS_FILTER = {
  key: "status",
  label: "Status",
  type: "dropdown",
  options: [],
};

const CREATED_DATE_FILTER = {
  key: "createdDateTime",
  label: "Select a date range",
  i18nKey: "ui.common.placeholders.SELECT_DATE_RANGE",
  type: "daterange",
};

const ACTION_FILTER = {
  key: "action",
  label: "Action",
  i18nKey: "ui.common.fields.ACTION",
  type: "dropdown",
  options: [],
};

const COUNTRY_FILTER = {
  key: "country",
  label: "Country",
  i18nKey: "ui.common.fields.COUNTRY",
  type: "dropdown",
  options: [],
};

const PRODUCT_NAME_FILTER = {
  key: "productLine",
  label: "Product line",
  i18nKey: "ui.common.fields.PRODUCT_LINE",
  type: "dropdown",
  options: [],
};

export const USER_MANAGEMENT_CONSTANTS = {
  // Delete Draft User flow
  DELETE_DRAFT_CONFIRMATION_TITLE: "ui.software_manager.success.SUCCESS_TITLE",
  DELETE_DRAFT_CONFIRMATION_MESSAGE: "Draft user deleted successfully.",
  DELETE_DRAFT_FAILURE_TITLE: COMMON_LABELS.ERROR,
  DELETE_DRAFT_FAILURE_MESSAGE: "Failed to delete draft user.",
};

export const GLOBAN_CONFIGURATION_CONSTANTS = {
  GLOBAN_CONFIGURATION_SUBMENU_TITLE: ["Software Globan number"],
  GLOBAN_CONFIGURATION_PAGE_TITLE: "Software Globan number",
  ADD_CONFIGURATION_BUTTON: "Add new Software Globan number",
  EDIT_CONFIGURATION_BUTTON: "Edit Software Globan number",
  OK_BUTTON: COMMON_LABELS.OK,
  GLOBAN_CONFIGURATION_ADD_SUCCESS_MESSAGE:
    "Software Globan Number has been created successfully.",
  GLOBAN_CONFIGURATION_EDIT_SUCCESS_MESSAGE:
    "Software Globan Number has been updated successfully.",
};

export const BIOMED_CONFIGURATION_CONSTANTS = {
  VALIDATION_DESCRIPTION_REQUIRED: "Description is required.",
  VALIDATION_CONTRACT_NAME_REQUIRED: "Contract name is required.",
  VALIDATION_SPECIAL_CHARACTERS: "Special characters are not allowed. Use only letters and numbers.",
  VALIDATION_DESCRIPTION_MAX_LENGTH: "Description cannot exceed 200 characters.",
  VALIDATION_CONTRACT_NAME_MAX_LENGTH: "Contract name cannot exceed 50 characters.",
  // Delete Draft User flow
  BIOMED_CONFIGURATION_SUBMENU_TITLE: ["Biomed Contract"],
  BIOMED_CONFIGURATION_PAGE_TITLE: "System Configuration",
  ADD_BIOMED_CONFIGURATION_BUTTON: "Add new Contract type",
  EDIT_BIOMED_CONFIGURATION_BUTTON: COMMON_LABELS.EDIT,
  ADD_BIOMED_POPUP_TITLE: "Add Biomed Contract ",
  EDIT_BIOMED_POPUP_TITLE: "Edit Biomed Contract ",
  BIOMED_CONFIGURATION_REGION: "Region",
  BIOMED_CONFIGURATION_COUNTRY: "Country",
  BIOMED_CONFIGURATION_CONTRACT: "Contract Name",
  OK_BUTTON: COMMON_LABELS.OK,
  BIOMED_CONTRACT_ADD_SUCCESS_MESSAGE: "Biomed Contract added successfully.",
  BIOMED_CONTRACT_EDIT_SUCCESS_MESSAGE: "Biomed Contract updated successfully.",
};

export const SOFTWARE_MANAGEMENT_CONSTANTS = {
  // Modal Headers
  PUBLISH_SUCCESS_TITLE: COMMON_LABELS.SUCCESS,
  EDIT_SUCCESS_TITLE: COMMON_LABELS.SUCCESS,
  DEACTIVATE_SUCCESS_TITLE: "Software Deactivated",
  REPUBLISH_SUCCESS_TITLE: "Software Republished",

  // Success Messages
  PUBLISH_SUCCESS_MESSAGE: "Software Published Successfully!",
  EDIT_SUCCESS_MESSAGE: "Software Edited Successfully!",
  DEACTIVATE_SUCCESS_MESSAGE: "Software Deactivated Successfully!",
  REPUBLISH_SUCCESS_MESSAGE: "Software Republished Successfully!",

  // Confirmation Dialogs
  CONFIRM_EDIT_TITLE: "Confirm Edit",
  CONFIRM_EDIT_MESSAGE:
    "Are you sure you want to save the changes to this software package?",
  CONFIRM_REPUBLISH_TITLE: "Confirm Republish",
  CONFIRM_REPUBLISH_MESSAGE:
    "Are you sure you want to republish this software package?",
  CONFIRM_DEACTIVATE_TITLE: "Confirm Deactivation",
  CONFIRM_DEACTIVATE_MESSAGE:
    "Are you sure you want to deactivate this software package?",

  // Modal Titles
  PUBLISH_SOFTWARE_TITLE: "Publish Software",
  DEACTIVATE_SOFTWARE_TITLE: "Deactivate Software",
  REPUBLISH_SOFTWARE_TITLE: "Republish Software",
  VIEW_PUBLISH_SOFTWARE_TITLE: "View Publish Software",
  DEACTIVATED_SOFTWARE_TITLE_FOR_TAC: "View Deactivated Package",

  // Button Labels
  CANCEL_BUTTON: COMMON_LABELS.CANCEL,
  CLOSE_BUTTON: COMMON_LABELS.CLOSE,
  PUBLISH_BUTTON: "Publish",
  DEACTIVATE_BUTTON: "Deactivate",
  SAVE_BUTTON: COMMON_LABELS.SAVE,
  YES_BUTTON: COMMON_LABELS.YES,
  NO_BUTTON: COMMON_LABELS.NO,
  OK_BUTTON: COMMON_LABELS.OK,
  UPLOAD_DOCUMENT: "Upload Document",
  UPLOADING: "Uploading...",
  DOWNLOAD: "Download",

  // Form Validation Messages
  VALIDATION_REGION_REQUIRED: "Please select at least one region",
  VALIDATION_COUNTRY_REQUIRED: "Please select at least one country",
  VALIDATION_DESELECT_ALL_USE_DEACTIVATE:
    "At least one region must be selected to proceed.",
  VALIDATION_REGION_WITHOUT_COUNTRY:
    "At least one country must be selected for each selected region to proceed.",
  VALIDATION_RELEASE_NOTES_URL: "Tech News URL must be a valid url starting with https://",
  VALIDATION_EQUIPMENT_NUMBER:
    "Software Equipment Global Number is required and should be alphanumeric",

  // Common Text
  STATUS_LOADING: "Loading...",
  NOT_AVAILABLE: "N/A",
  PLACEHOLDER_SELECT: COMMON_LABELS.SELECT,
  SELECTED_ITEMS_LABEL: "{0} selected",
  REMOVE_FILE_TOOLTIP: "Remove file",
  DOWNLOAD_FILE_TOOLTIP: "Download file",
};

// Common Constants
export const COMMON_DATE_FORMAT = "dd/mm/yy";
export const COMMON_DATE_FORMAT_EDIT_INJECTOR = "dd M, yy";

export const CUSTOMER_ONBOARDING_CONSTANTS = {
  // Titles and Headers
  MODULE_TITLE: "Customer Onboarding",
  CUSTOMER_TAB_TITLE: "Customers",
  AUDIT_LOG_TAB_TITLE: "Audit Log",
  EXPORT_BUTTON_LABEL: "Export",

  // Add customer flow
  ADD_CUSTOMER_BUTTON_LABEL: "Add Customer",
  ADD_CUSTOMER_MODAL_TITLE: "Customer Onboarding", // Reused for adding
  CUSTOMER_CREATED_TITLE: "Customer Added",
  ADD_PRODUCT_LINE_INJECTORS_BUTTON_LABEL: "Add Product Line & Injectors",
  ADD_ANOTHER_CUSTOMER_BUTTON_LABEL: "Add Another Customer",
  ADD_INJECTORS_MODAL_TITLE: "Add Injectors",
  ADD_INJECTORS_BUTTON_LABEL: "Add Injector",
  INJECTORS_ADDED_TITLE: "Injector Added",
  CUSTOMER_CREATED_SUCCESS_MESSAGE:
    "The new customer has been successfully added to the system.", // Renamed from NEW_CUSTOMER_ADDED_SUCCESS
  CUSTOMER_CREATED_SUCCESS_TITLE: "Customer Created Successfully!", // Renamed from CUSTOMER_CREATED_TITLE

  // Edit customer flow
  EDIT_CUSTOMER_MODAL_TITLE: "Edit Customer",
  EDIT_PARENT_CUSTOMER_MODAL_TITLE: "Edit Parent Customer",
  CUSTOMER_EDITED_TITLE: "Customer Edited",
  INJECTORS_EDITED_TITLE: "Injector Edited",
  EDIT_INJECTORS_MODAL_TITLE: "Edit Injectors",

  BACK_TO_CUSTOMER_LISTING: "Back To Customer Listing",
  EDIT_BUTTON_LABEL: "Edit",
  REMOVE_BUTTON_LABEL: "Remove",
  VIEW_BUTTON_LABEL: "View",
  CHANGE_EXPIRY_DATE_BUTTON_LABEL: "Change Expiry Date",
  ASSIGN_BUTTON_LABEL: "Assign",
  TRANSFER_BUTTON_LABEL: "Transfer",
  ASSIGN_ROLE_BUTTON_LABEL: "Assign Role",
  NO_INJECTORS_FOUND: "No Injectors Found",
  NO_INJECTORS_ADDED: "No Injectors Added",
  SAVE_BUTTON_LABEL: "Save",
  CONTINUE_BUTTON_LABEL: "Continue",
  CANCEL_BUTTON_LABEL: "Cancel",
  CLOSE_BUTTON_LABEL: "Close",
  SUBMIT_BUTTON_LABEL: "Submit",
  DONE_BUTTON_LABEL: "Done",

  // Messages
  LOGIN_SUCCESS_MESSAGE: "Login successful!",
  SESSION_EXPIRED_DETAIL: "You have been logged out.",
  PASSWORD_RESET_TOKEN_MISSING: "Password reset token is missing.",
  FORM_CORRECTION_WARNING: "Please correct the form errors.",
  NEW_CUSTOMER_ADDED_SUCCESS: "New Customer Added Successfully",
  CUSTOMER_EDITED_SUCCESS: "Customer Edited Successfully",
  INJECTORS_ADDED_SUCCESS: "Injector Added Successfully",
  INJECTORS_EDITED_SUCCESS: "Injector Edited successfully",
  CUSTOMER_DETAILS_UPDATED_SUCCESS: "Customer Updated Successfully!",
  NOTIFICATION_SETTINGS_SAVED_SUCCESS:
    "Notification settings saved successfully",
  FILL_ALL_REQUIRED_INJECTOR_FIELDS:
    "Please fill all required fields for the injector.",
  ADD_AT_LEAST_ONE_INJECTOR: "Please add at least one injector to continue.",
  INVALID_INJECTOR_SELECTION_NOTICE:
    "You cannot add injectors with missing or invalid data. Please provide valid values in the Customer Onboarding section.",
  NO_MATCHING_RECORDS_FOUND: "No matching records found.",

  // Table Headers (Customer Onboarding Component)
  TABLE_HEADER_SNO: "S.No",
  TABLE_HEADER_PARENT_CUSTOMER_NAME: "Parent Customer Name",
  TABLE_HEADER_ACCOUNT_SMG_NUMBER: "Parent Account Number",
  TABLE_HEADER_CUSTOMER_NAME: "Customer Name",
  TABLE_HEADER_CUSTOMER_LOCATION: "Customer Location",
  TABLE_HEADER_CUSTOMER_SAP_NUMBER: "Customer Account Number",
  TABLE_HEADER_ACTION: "Action",
  TABLE_HEADER_USER_NAME: "User Name",
  TABLE_HEADER_EMAIL_ID: "Email ID",
  TABLE_HEADER_ROLE_TYPE: "Role Type",
  TABLE_HEADER_TIMESTAMP: "Timestamp",

  // Table Headers (Customer Listing Component - Injectors)
  TAB_HEADER_INJECTORS: "Injectors",
  TABLE_HEADER_PRODUCT_LINE: "Product Line",
  TABLE_HEADER_PRODUCT_MODALITY: "Product Modality",
  TABLE_HEADER_SYSTEM_SERIAL_NUMBER: "System serial number",
  TABLE_HEADER_SRU_SERIAL_NUMBER: "SRU head serial number",
  TABLE_HEADER_CRU_SERIAL_NUMBER: "CRU AIOC serial number",
  TABLE_HEADER_INJECTOR_ID: "Injector ID",
  TABLE_HEADER_PASSLINK_SYSTEM_ID: "Passlink System ID",
  TABLE_HEADER_MASTER_ASSET_NUMBER: "Master Asset Number",
  TABLE_HEADER_CONTRACT_NAME: "Contract Name",
  TABLE_HEADER_CONTRACT_EXPIRY_DATE: "Contract Expiry Date",
  TABLE_HEADER_ACTIONS: "Actions",

  // Form Labels (Edit Customer Component)
  FORM_LABEL_PARENT_CUSTOMER_NAME: "Parent Customer Name",
  /**
   * Allow-list for Parent Customer Name and Customer Name.
   *
   * Permits Unicode letters/marks (so international names such as "Hopital
   * Saint-Louis" or "Arztehaus Nord" are accepted), digits, spaces, and the
   * punctuation that legitimately occurs in organisation names: . , - ' & ( ) /
   * Everything else (@ ! # $ % ^ * = < > { } [ ] | ` ~ ; _ + " and control
   * characters) is rejected.
   *
   * The leading look-ahead requires at least one letter or digit, so values
   * made up purely of punctuation (e.g. "..." or "---") are also rejected.
   *
   * NOTE: this must stay a RegExp object rather than a string. Angular's
   * `pattern` validator compiles string patterns via `new RegExp(str)` with no
   * flags, which would strip the `u` flag and break the `\p{...}` escapes.
   */
  FORM_REGEX_PARENT_CUSTOMER_NAME:
    /^(?=.*[\p{L}\p{N}])[\p{L}\p{M}\p{N} .,'&()/-]+$/u,
  FORM_LABEL_ACCOUNT_SMG_NUMBER: "Parent Account Number",
  FORM_LABEL_CUSTOMER_NAME: "Customer Name",
  FORM_LABEL_CUSTOMER_LOCATION: "Customer Location",
  FORM_LABEL_CUSTOMER_SAP_NUMBER: "Customer Account Number",
  FORM_REGEX_CUSTOMER_SAP_NUMBER: "^[a-zA-Z0-9-]+$",

  // Form Labels (Add/Edit Injectors Component)
  FORM_LABEL_PRODUCT_LINE: "Product Line",
  FORM_LABEL_PRODUCT_MODALITY: "Product Modality",
  FORM_LABEL_SYSTEM_SERIAL_NUMBER: "System serial number",
  FORM_LABEL_SRU_SERIAL_NUMBER: "SRU head serial number",
  FORM_LABEL_CRU_SERIAL_NUMBER: "CRU AIOC serial number",
  FORM_LABEL_INJECTOR_ID: "Injector ID",
  FORM_LABEL_PASSLINKSYSTEM_ID: "PassLink System ID",
  FORM_LABEL_MASTER_ASSET_NUMBER: "Master Asset Number",
  FORM_LABEL_CONTRACT_NAME: "Contract Name",
  FORM_LABEL_CONTRACT_EXPIRY_DATE: "Contract Expiry Date",
  FORM_LABEL_POSTAL_CODE: "Postal Code",
  // Postal Code: letters, numbers, spaces and hyphens only
  FORM_REGEX_POSTAL_CODE: "^[a-zA-Z0-9 -]+$",
  POSTAL_CODE_INVALID_MESSAGE:
    "Postal Code can only contain letters, numbers, spaces and hyphens",

  // Placeholders
  PLACEHOLDER_SELECT: "Select",
  PLACEHOLDER_DATE: "dd/mmm/yyyy",
  PLACEHOLDER_ENTER_TEXT: "Enter text...",
  PLACEHOLDER_SEARCH: "Search...",
  PLACEHOLDER_PARENT_CUSTOMER_NAME: "Parent Customer Name",
  PLACEHOLDER_ACCOUNT_SMG_NUMBER: "Parent Account Number",
  PLACEHOLDER_CUSTOMER_NAME: "Customer Name",
  PLACEHOLDER_CUSTOMER_SAP_NUMBER: "Customer Account Number",
  PLACEHOLDER_SYSTEM_SERIAL_NUMBER: "System serial number",
  PLACEHOLDER_SRU_SERIAL_NUMBER: "SRU head serial number",
  PLACEHOLDER_CRU_SERIAL_NUMBER: "CRU AIOC serial number",
  PLACEHOLDER_INJECTOR_ID: "Injector ID",
  PLACEHOLDER_PASSLINKSYSTEM_ID: "PassLink System ID",
  PLACEHOLDER_MASTER_ASSET_NUMBER: "Master Asset Number",
  PLACEHOLDER_CONTRACT_NAME: "Contract Name",

  // Routes
  ROUTE_CUSTOMER_ONBOARDING: "/customer-onboarding",
  ROUTE_CUSTOMER_LISTING: "/customer-listing", // Base route, needs :sno appended
  ROUTE_HOME: "/home",
  ROUTE_LOGIN: "/login",

  // Numbers/Counts
  TABLE_ROWS_DEFAULT: 10,
  TABLE_ROWS_INJECTORS: 3,

  // Other
  DATE_FORMAT: COMMON_DATE_FORMAT,
  DATE_FORMAT_EDIT_INJECTOR: COMMON_DATE_FORMAT_EDIT_INJECTOR,
  GLOBAL_FILTER_CONTAINS: "contains",
};

/**
 * Transloco translation keys for the Customer Onboarding module.
 * These map 1:1 to the keys returned by the backend translation catalog
 * (GET /translations/{locale}) and are intentionally kept verbatim — including
 * the backend's `ui.cusomter_onboarding` typo for MASTER_ASSET_NO.
 */
export const CUSTOMER_ONBOARDING_I18N = {
  PAGE_TITLE: "ui.common.modules.CUSTOMER_ONBOARDING",
  CUSTOMERS: "ui.customer_onboarding.CUST",
  ADD_CUSTOMER: "ui.customer_onboarding.ADD_CUST",
  CUSTOMER_LOCATION: "ui.common.fields.CUSTOMER_LOCATION",
  BACK_TO_CUSTOMER_LISTING: "ui.customer_onboarding.view.BACK_CUST_LIST",
  S_NO: "ui.customer_onboarding.S_NO",
  ACTIONS: "ui.common.ACTIONS",
  INJECTORS: "ui.customer_onboarding.view.INJ",
  ADD_INJECTOR: "ui.common.actions.ADD_INJECTOR",
  ADD_INJECTORS_TITLE: "ui.common.actions.ADD_INJECTOR",
  PRODUCT_LINE: "ui.common.fields.PRODUCT_LINE",
  INJECTOR_ID: "ui.common.fields.INJECTOR_ID",
  ACTION: "ui.common.fields.ACTION",
  NO_MATCHING_RECORDS: "ui.analytics.common.NO_MATCHIG_RECORDS_FOUND",
  REMOVE: "ui.common.REMOVE",
  SR_NO: "ui.common.fields.SR_NO",
  PRODUCT_MODALITY: "ui.common.fields.PRODUCT_MODALITY",
  SYSTEM_SERIAL_NUMBER: "ui.common.fields.SYSTEM_SERIAL_NUMBER",
  SRU_HEAD_SERIAL_NUMBER: "ui.common.fields.SRU_HEAD_SERIAL_NUMBER",
  CRU_AIOC_SERIAL_NUMBER: "ui.common.fields.CRU_AIOC_SERIAL_NUMBER",
  PASSLINK_SYSTEM_ID: "ui.common.fields.PASSLINK_SYSTEM_ID",
  MASTER_ASSET_NUMBER: "ui.cusomter_onboarding.view.MASTER_ASSET_NO",
  CONTRACT_NAME: "ui.common.fields.CONTRACT_NAME",
  CONTRACT_EXPIRY_DATE: "ui.common.fields.CONTRACT_EXPIRY_DATE",
  EDIT_CUSTOMER: "ui.customer_onboarding.edit.EDIT_CUST",
  EDIT_INJECTORS: "ui.customer_onboarding.edit_injector.EDIT_INJ_TITLE",
  INJECTORS_EDITED_TITLE:
    "ui.customer_onboarding.edit_injector.INJ_EDITED_TITLE",
  INJECTORS_EDITED_SUCCESS:
    "ui.customer_onboarding.edit_injector.INJ_EDITED_SUCCESS",
  ONLY_LETTERS_AND_NUMBERS:
    "ui.customer_onboarding.edit_injector.ONLY_LETTERS_AND_NUMBERS",
  CANNOT_BE_NA: "ui.customer_onboarding.edit_injector.CANNOT_BE_NA",
  CONTINUE: "ui.common.actions.CONTINUE",
  CANCEL: "ui.common.actions.CANCEL",
  PARENT_CUSTOMER_NAME: "ui.common.fields.PARENT_CUSTOMER_NAME",
  PARENT_ACCOUNT_NUMBER: "ui.analytics.customer_analytics.PARENT_ACC_NO",
  CUSTOMER_NAME: "ui.common.fields.CUSTOMER_NAME",
  CUSTOMER_ACCOUNT_NUMBER: "ui.common.fields.CUSTOMER_ACCOUNT_NUMBER",
  PRODUCT_SUB_ASSEMBLY: "ui.common.fields.PRODUCT_SUB_ASSEMBLY",
  POSTAL_CODE: "ui.common.fields.POSTAL_CODE",
  NUMBER_OF_INJECTORS: "ui.customer_onboarding.NUMBER_OF_INJECTORS",
  VIEW: "ui.common.actions.VIEW",
  EDIT: "ui.common.actions.EDIT",
  SEARCH: "ui.common.actions.SEARCH",
  SEARCH_CUSTOMERS:
    "ui.user_management.create_user.customer_details.SEARCH_CUSTOMERS",
  SELECT: "ui.common.actions.SELECT",
  APPLY: "ui.common.actions.APPLY",
  RESET: "ui.common.actions.RESET",
  LOAD_MORE: "ui.notification.LOAD_MORE",
  ITEMS_SELECTED: "ui.common.placeholders.ITEMS_SELECTED",
  NOTICE: "ui.user_management.create_user.add_injector.NOTICE",
  NO_INJECTORS_AVAILABLE:
    "ui.user_management.add_edit.NO_INJ_AVAILBLE",
  NO_RESULTS_FOUND: "ui.common.messages.NO_RES_FOUND",
  UNABLE_TO_LOAD_CUSTOMERS: "ui.customer_onboarding.UNABLE_TO_LOAD_CUSTOMERS",
  LOADING_CUSTOMERS: "ui.customer_onboarding.LOADING_CUSTOMERS",
  LOADING_MORE_CUSTOMERS: "ui.customer_onboarding.LOADING_MORE_CUSTOMERS",
  LOADING_INJECTORS: "ui.customer_onboarding.LOADING_INJECTORS",
  INVALID_INJECTOR_SELECTION:
    "ui.customer_onboarding.INVALID_INJECTOR_SELECTION",
  INVALID_INJECTOR_SELECTION_CUSTOMER_ADMIN:
    "ui.customer_onboarding.INVALID_INJECTOR_SELECTION_CUSTOMER_ADMIN",
  CUSTOMER_DESELECTION_WARNING:
    "ui.user_management.add_edit.WARNING_MSG",
  SELECT_ALL_INJECTORS: "ui.customer_onboarding.SELECT_ALL_INJECTORS",
  SELECT_ALL_LIMIT_EXCEEDED:
    "ui.customer_onboarding.SELECT_ALL_LIMIT_EXCEEDED",
  SELECT_ALL_FAILED: "ui.customer_onboarding.SELECT_ALL_FAILED",
  SELECT_ALL_TOO_BROAD: "ui.customer_onboarding.SELECT_ALL_TOO_BROAD",
  SELECT_ALL_TIMEOUT: "ui.customer_onboarding.SELECT_ALL_TIMEOUT",
  SELECTING_ALL: "ui.customer_onboarding.SELECTING_ALL",
  RETRY: "ui.common.actions.RETRY",
  DISMISS: "ui.common.actions.DISMISS",
  CLEAR_ALL_SELECTIONS: "ui.customer_onboarding.CLEAR_ALL_SELECTIONS",
  SELECTIONS_CLEARED: "ui.customer_onboarding.SELECTIONS_CLEARED",
  UNDO: "ui.common.actions.UNDO",
  SELECT_ALL_INJECTORS_FOR:
    "ui.customer_onboarding.SELECT_ALL_INJECTORS_FOR",
  SELECT_INJECTOR: "ui.customer_onboarding.SELECT_INJECTOR",
  IS_REQUIRED: "ui.common.field.IS_REQUIRED",
  ADD_CUSTOMER_SUCCESS_MSG:
    "ui.customer_onboarding.add_customer.ADD_CUST_SUCCESS_MSG",
  ADD_CUSTOMER_TITLE: "ui.customer_onboarding.add_customer.ADD_CUST_TITLE",
  ADD_PRODUCT_LINE_INJECTORS:
    "ui.customer_onboarding.add_customer.ADD_PRODUCT_LINE_INJ",
  ADD_ANOTHER_CUSTOMER: "ui.customer_onboarding.add_customer.ADD_ANOTHER_CUST",
  CUSTOMER_ADDED: "ui.customer_onboarding.add_customer.CUST_ADDED",
  VALID_MSG1: "ui.customer_onboarding.add_edit_customer.VALID_MSG1",
  VALID_MSG1_MODIFIED: "ui.customer_onboarding.add_edit_customer.VALID_MSG1_MODIFIED",
  VALID_MSG2: "ui.customer_onboarding.add_edit_customer.VALID_MSG2",
  VALID_MSG3: "ui.customer_onboarding.add_edit_customer.VALID_MSG3",
  VALID_MSG4: "ui.customer_onboarding.add_edit_customer.VALID_MSG4",
  VALID_MSG5: "ui.customer_onboarding.add_edit_customer.VALID_MSG5",
  VALID_MSG6: "ui.customer_onboarding.add_edit_customer.VALID_MSG6",
  VALID_MSG7: "ui.customer_onboarding.add_edit_customer.VALID_MSG7",
  VALID_MSG8: "ui.customer_onboarding.add_edit_customer.VALID_MSG8",
  VALID_MSG9: "ui.customer_onboarding.add_edit_customer.VALID_MSG9",
  VALID_MSG10: "ui.customer_onboarding.add_edit_customer.VALID_MSG10",
  VALID_MSG11: "ui.customer_onboarding.add_edit_customer.VALID_MSG11",
  VALID_MSG12: "ui.customer_onboarding.add_edit_customer.VALID_MSG12",
  VALID_MSG13: "ui.customer_onboarding.add_edit_customer.VALID_MSG13",
  VALID_MSG14: "ui.customer_onboarding.add_edit_customer.VALID_MSG14",
  VALID_MSG20: "ui.customer_onboarding.add_edit.VALID_MSG20",
  TOOLTIP: "ui.customer_onboarding.add_edit_customer.TOOLTIP",
  TOOLTIP1: "ui.customer_onboarding.add_edit_customer.TOOLTIP1",
  PRODUCT_LINE_REQUIRED: "ui.common.messages.PRODUCT_LINE_REQUIRED",
  PRODUCT_MODALITY_REQUIRED:
    "ui.product_onboarding.validation.PRODUCT_MODALITY_REQUIRED",
  CONTRACT_NAME_REQUIRED:
    "ui.customer_onboarding.add_injector.validation.CONTRACT_NAME_REQUIRED",
  CONTRACT_EXPIRY_DATE_REQUIRED:
    "ui.customer_onboarding.add_injector.validation.CONTRACT_EXPIRY_DATE_REQUIRED",
  POSTAL_CODE_REQUIRED:
    "ui.customer_onboarding.add_injector.validation.POSTAL_CODE_REQUIRED",
  POSTAL_CODE_INVALID:
    "ui.customer_onboarding.add_injector.validation.POSTAL_CODE_INVALID",
  MANDATORY_FIELDS_NOTICE:
    "ui.customer_onboarding.add_injector.validation.MANDATORY_FIELDS_NOTICE",
  // Common actions
  OK: "ui.common.OK",
  CLOSE: "ui.common.actions.CLOSE",
  // Add injector success dialog
  INJECTOR_ADDED_TITLE: "ui.customer_onboarding.add_injector.INJ_ADDED_TITLE",
  INJECTOR_ADDED_SUCCESS:
    "ui.customer_onboarding.add_injector.INJ_ADDED_SUCCESS",
  INJECTOR_ADDED_TO_LIST:
    "ui.customer_onboarding.add_injector.INJ_ADDED_TO_LIST",
  // Add injector failure dialog
  INJECTOR_ADDITION_FAILED_TITLE:
    "ui.customer_onboarding.add_injector.INJ_ADDITION_FAILED_TITLE",
  ALL_INJECTOR_ADDITIONS_FAILED:
    "ui.customer_onboarding.add_injector.ALL_INJ_ADDITIONS_FAILED",
  // Remove injector confirmation dialog
  REMOVE_INJECTOR_TITLE: "ui.customer_onboarding.remove_injector.TITLE",
  REMOVE_INJECTOR_MESSAGE_SINGLE:
    "ui.customer_onboarding.remove_injector.MESSAGE_SINGLE",
  REMOVE_INJECTOR_MESSAGE_MULTIPLE:
    "ui.customer_onboarding.remove_injector.MESSAGE_MULTIPLE",
  // Customer updated success dialog
  CUSTOMER_UPDATED_TITLE:
    "ui.customer_onboarding.edit_customer.CUST_UPDATED_TITLE",
  CUSTOMER_UPDATED_MESSAGE:
    "ui.customer_onboarding.edit_customer.CUST_UPDATED_MSG",
} as const;

/**
 * Transloco translation keys for Software Analytics (and shared Overview)
 * recent self-upgrade / publish table headers.
 */
export const SOFTWARE_ANALYTICS_I18N = {
  SR_NO: "ui.analytics.software_analytics.SR_NO",
  INJECTOR_ID: "ui.common.fields.INJECTOR_ID",
  PRODUCT_LINE: "ui.common.fields.PRODUCT_LINE",
  DATE_TIME: "ui.analytics.software_analytics.DATE_TIME",
  CUSTOMER_NAME: "ui.common.fields.CUSTOMER_NAME",
  CURR_VERSION: "ui.analytics.software_analytics.CURR_VERSION",
  PREV_VERSION: "ui.analytics.software_analytics.PREV_VERSION",
  STATUS: "ui.common.fields.STATUS",
  SUB_ASSEMBLY: "ui.analytics.software_analytics.SUB_ASSEMBLY",
  SOFTWARE_TYPE: "ui.common.fields.SOFTWARE_TYPE",
  RELEASE_DATE: "ui.common.fields.RELEASE_DATE",
  NO_OF_DOWNLOADS: "ui.analytics.software_analytics.NO_OF_DOWNLOADS",
  MOST_DOWNLOADS: "ui.analytics.software_analytics.MOST_DOWNLOADS",
  SW_DOWNLOADS: "ui.analytics.software_analytics.SW_DOWNLOADS",
  SW_UPGRADES: "ui.analytics.software_analytics.SW_UPGRADES",
} as const;

/** Home / Analytics shell labels (sub-menus, filters, empty states). */
export const HOME_ANALYTICS_I18N = {
  OVERVIEW: "ui.analytics.OVERVIEW",
  SOFTWARE_ANALYTICS: "ui.analytics.SW_ANALYTICS",
  USER_ANALYTICS: "ui.analytics.USR_ANALYTICS",
  CUSTOMER_ANALYTICS: "ui.analytics.CSTMR_ANALYTICS",
  PASSLINK_MOBILE: "ui.audit_log.module.PASSLINK_MOBILE",
  VIRTUAL_CARE: "ui.analytics.VIRTUAL_CARE",
  INJECTOR_DATA_ANALYTICS: "ui.analytics.INJ_DATA_ANALYTICS",
  PRODUCT_LINE: "ui.common.fields.PRODUCT_LINE",
  USER_ROLE: "ui.common.fields.USER_ROLE",
  USER: "HME_001_home.user",
  REGION: "ui.common.fields.REGION",
  COUNTRY: "ui.common.fields.COUNTRY",
  LOADING: "ui.analytics.common.LOADING",
  NO_DATA_AVAILABLE: "ui.analytics.user_analytics.NO_DATA_AVAILABLE",
  VIEW: "ui.common.actions.VIEW",
  ITEMS_SELECTED: "ui.common.placeholders.ITEMS_SELECTED_LONG",
  SELECT_DATE_RANGE: "ui.common.placeholders.SELECT_DATE_RANGE",
  COMING_SOON: "ui.analytics.common.COMING_SOON",
  INJECTORS_UPDATED: "ui.analytics.overview.INJECTORS_UPDATED",
  INJECTORS_YET_TO_UPDATE: "ui.analytics.overview.INJECTORS_YET_TO_UPDATE",
  REFRESH: "ui.common.actions.REFRESH",
} as const;

/** User Analytics + Passlink Mobile shared user-table headers / chart labels. */
export const USER_ANALYTICS_I18N = {
  SR_NO: "ui.common.fields.SR_NO",
  USER_ID: "ui.analytics.user_analytics.USR_ID",
  FULL_NAME: "ui.common.fields.FULL_NAME",
  EMAIL_ID: "ui.common.fields.EMAIL_ID",
  ROLE: "ui.common.fields.ROLE",
  ASSIGNED_REGION: "ui.common.fields.ASSIGNED_REGION",
  ASSIGNED_COUNTRY_IES: "ui.common.fields.ASSIGNED_COUNTRY_IES",
  NO_OF_LOGINS: "ui.analytics.user_analytics.NO_OF_LOGINS",
  NO_OF_DOWNLOADS: "ui.analytics.software_analytics.NO_OF_DOWNLOADS",
  PRODUCT_LINE: "ui.common.fields.PRODUCT_LINE",
  ACTIVE_USERS: "ui.analytics.user_analytics.ACTIVE_USERS",
  SOFTWARE_DOWNLOADS: "ui.analytics.software_analytics.SW_DOWNLOADS",
} as const;

/** Customer Analytics table headers. */
export const CUSTOMER_ANALYTICS_I18N = {
  SR_NO: "ui.common.fields.SR_NO",
  PARENT_CUSTOMER_NAME: "ui.common.fields.PARENT_CUSTOMER_NAME",
  PARENT_ACCOUNT_NUMBER: "ui.analytics.customer_analytics.PARENT_ACC_NO",
  CUSTOMER_NAME: "ui.common.fields.CUSTOMER_NAME",
  CUSTOMER_LOCATION: "ui.common.fields.CUSTOMER_LOCATION",
  CUSTOMER_ACCOUNT_NUMBER: "ui.common.fields.CUSTOMER_ACCOUNT_NUMBER",
  NO_OF_CONTRACTS: "ui.analytics.customer_analytics.NO_OF_CONTRACTS",
  NO_OF_USERS: "ui.analytics.customer_analytics.NO_OF_USRS",
} as const;

/** Injector Data Analytics table headers. */
export const INJECTOR_DATA_ANALYTICS_I18N = {
  SR_NO: "ui.common.fields.SR_NO",
  PRODUCT_LINE: "ui.common.fields.PRODUCT_LINE",
  FILE_NAME: "ui.audit_log.column.FILE_NAME",
  FILE_SIZE: "ui.audit_log.column.FILE_SIZE",
  REQUEST_NO: "ui.audit_log.column.REQUEST_NO",
  CUSTOMER_NAME: "ui.common.fields.CUSTOMER_NAME",
  STATUS: "ui.common.fields.STATUS",
} as const;

/** Virtual Care injector grid / filters. */
export const VIRTUAL_CARE_I18N = {
  INJECTOR_STATUS: "ui.analytics.virtual_care.INJ_STATUS",
  SERIAL_NUMBER: "ui.analytics.virtual_care.SERIAL_NUMBER",
  MAC_ID: "ui.analytics.virtual_care.MAC_ID",
  MODEL: "ui.analytics.virtual_care.MODEL",
  PRODUCT_LINE: "ui.common.fields.PRODUCT_LINE",
  CUSTOMER: "ui.analytics.virtual_care.CUSTOME",
  REGION: "ui.common.fields.REGION",
  COUNTRY: "ui.common.fields.COUNTRY",
  CURRENT_PSV: "ui.analytics.virtual_care.CURRENT_PSV",
  REPORT_TIMESTAMP: "ui.analytics.virtual_care.REPORT_TIMESTAMP",
  PACKAGE_ID: "ui.analytics.virtual_care.PCKG_ID",
  PACKAGE_VERSION: "ui.analytics.virtual_care.PCKG_VERSION",
  PACKAGE_DOWNLOAD_DATE: "ui.analytics.virtual_care.PCKG_DWNLD_DATE",
  STATUS: "ui.analytics.virtual_care.STATUS",
  ERRORS: "ui.analytics.virtual_care.ERRORS",
  IS_SW_INSTALLED: "ui.analytics.virtual_care.IS_SW_INSTALLED",
  INSTALLED_DATE: "ui.analytics.virtual_care.INSTALLED_DATE",
  INSTALLATION_STATUS: "ui.analytics.virtual_care.INSTALLATION_STATUS",
  INJECTOR_CURRENT_SW_VERSION: "ui.analytics.virtual_care.INJECTOR_CURRENT_SW_VERSION",
  STATUS_SUCCESS: "ui.analytics.virtual_care.STATUS_SUCCESS",
  STATUS_COMPLETED: "ui.analytics.virtual_care.STATUS_COMPLETED",
  STATUS_FAILED: "ui.analytics.virtual_care.STATUS_FAILED",
  STATUS_FAILURE: "ui.analytics.virtual_care.STATUS_FAILURE",
  STATUS_PENDING: "ui.analytics.virtual_care.STATUS_PENDING",
  STATUS_INELIGIBLE: "ui.analytics.virtual_care.STATUS_INELIGIBLE",
  STATUS_ABORTED: "ui.analytics.virtual_care.STATUS_ABORTED",
  VALUE_NA: "ui.common.NA",
  VIEW: "ui.common.actions.VIEW",
  REFRESH: "ui.common.actions.REFRESH",
} as const;

/**
 * Transloco translation keys for the User Management module.
 * Map 1:1 to the keys returned by the backend translation catalog.
 */
export const USER_MANAGEMENT_I18N = {
  PAGE_TITLE:               "ui.common.modules.USER_MANAGEMENT",
  // Sub-menus
  MENU_USERS:               "ui.user_management.menu.USERS",
  MENU_DRAFT:               "ui.common.status.DRAFT",
  MENU_PERMISSION_SETTINGS: "ui.user_management.menu.PERMISSION_SETTINGS",
  // Table columns
  S_NO:                     "ui.common.fields.SR_NO",
  USER_ID:                  "ui.user_management.table.USER_ID",
  FULL_NAME:                "ui.common.fields.FULL_NAME",
  EMAIL_ID:                 "ui.common.fields.EMAIL_ID",
  ROLE:                     "ui.common.fields.ROLE",
  ROLE_TYPE:                "ui.common.fields.ROLE_TYPE",
  ASSIGNED_REGION:          "ui.common.fields.ASSIGNED_REGION",
  ASSIGNED_COUNTRY:         "ui.common.fields.ASSIGNED_COUNTRY",
  ASSIGNED_COUNTRIES:       "ui.common.fields.ASSIGNED_COUNTRIES",
  ASSIGNED_COUNTRY_IES:     "ui.common.fields.ASSIGNED_COUNTRY_IES",
  ORG_NAME:                 "ui.common.fields.ORGANIZATION_NAME",
  INJ_DATA_ACCESS:          "ui.common.fields.INJECTOR_DATA_ACCESS",
  MAND_SW_PCKG:             "ui.profile.MANDTRY_SW_PCKG",
  ENT_SW_PCKG:              "ui.user_management.create_user.user_details.ENTITLEMENT_SOFTWARE_PACKAGES",
  USER_COUNTRY:             "ui.common.fields.USER_COUNTRY",
  STATUS:                   "ui.common.fields.STATUS",
  ACTIONS:                  "ui.common.ACTIONS",
  TOOLTIP_ASSIGNED_REGIONS: "ui.user_management.table.TOOLTIP_ASSIGNED_REGIONS",
  // Row actions
  VIEW:                     "ui.common.actions.VIEW",
  RESUME:                   "ui.user_management.common.RESUME",
  DELETE:                   "ui.user_management.common.DELETE",
  // Toolbar / buttons
  EXPORT:                   "ui.common.actions.EXPORT",
  CREATE_USER:              "ui.common.actions.CREATE_USER",
  CANCEL:                   "ui.common.actions.CANCEL",
  SUBMIT:                   "ui.common.actions.SUBMIT",
  CONTINUE:                 "ui.common.actions.CONTINUE",
  SAVE:                     "ui.common.actions.SAVE",
  DONE:                     "ui.common.actions.DONE",
  CLOSE:                    "ui.common.actions.CLOSE",
  OK:                       "ui.common.OK",
  // Status badge labels
  ACTIVE:                   "ui.common.status.ACTIVE",
  INACTIVE:                 "ui.common.status.INACTIVE",
  PENDING:                  "ui.common.status.PENDING",
  DRAFT:                    "ui.common.status.DRAFT",
  // Dialogs / modals
  BULK_UPLOAD:              "ui.user_management.BULK_UPLOAD",
  SAVE_AS_DRAFT:            "ui.user_management.SAVE_AS_DRAFT",
  CHANGE_PASSLINK_END_DATE: "ui.user_management.CHANGE_PASSLINK_END_DATE",
  DELETE_DRAFT_TITLE:       "ui.user_management.delete_draft.TITLE",
  DELETE_DRAFT_MESSAGE:     "ui.user_management.delete_draft.MESSAGE",
  DELETE_CONFIRM_TITLE:     "ui.user_management.delete_confirmation.TITLE",
  DELETE_CONFIRM_MESSAGE:   "ui.user_management.delete_confirmation.MESSAGE",
  // Permission settings
  PS_SELECT_USER:           "ui.user_management.permission_settings.SELECT_USER",
  PS_USER_RESPONSIBLE_REGION:  "ui.user_management.permission_settings.USER_RESPONSIBLE_REGION",
  PS_USER_RESPONSIBLE_COUNTRY: "ui.user_management.permission_settings.USER_RESPONSIBLE_COUNTRY",
  PS_SELECT_PERMISSIONS:    "ui.user_management.permission_settings.SELECT_PERMISSIONS",
  PS_START_DATE:            "ui.user_management.permission_settings.START_DATE",
  PS_END_DATE:              "ui.user_management.permission_settings.END_DATE",
  PS_SEARCH_USERS:          "ui.user_management.permission_settings.SEARCH_USERS_PLACEHOLDER",
  PS_NO_USERS_FOUND:        "ui.user_management.permission_settings.NO_USERS_FOUND",
  PS_SELECT_USER_FIRST:     "ui.user_management.permission_settings.SELECT_USER_FIRST",
  PS_DIALOG_HEADER:         "ui.user_management.permission_settings.DIALOG_HEADER",
  PS_UPDATED_SUCCESS:       "ui.user_management.permission_settings.UPDATED_SUCCESS",
  ITEMS_SELECTED:           "ui.common.placeholders.ITEMS_SELECTED",
  // Create user
  CREATE_NEW_USER:          "ui.user_management.create_user.CREATE_NEW_USER",
  SELECT_USER_ROLE:         "ui.user_management.create_user.user_role.SELECT_USER_ROLE",
  USER_CREATED:             "ui.user_management.create_user.USER_CREATED",
  NEW_USER_CREATED_SUCCESS: "ui.common.messages.NEW_USER_CREATED_SUCCESS",
  ENTER_INJECTOR_ID:        "ui.user_management.create_user.ENTER_INJECTOR_ID",
  EMAIL_ALREADY_EXISTS:     "ui.user_management.create_user.EMAIL_ALREADY_EXISTS",
  BACK_TO_LISTING:          "ui.user_management.add_edit_customer.BACK_USR_LISTING",
  ACTIVE_INJ:               "ui.user_profile.tabs.ACTIVE_INJ",
  CUSTOMER_NAME:            "ui.common.fields.CUSTOMER_NAME",
  TABLE_HEADER_PARENT_CUSTOMER_NAME: "ui.common.fields.PARENT_CUSTOMER_NAME",
  FIRST_NAME:               "ui.user_management.create_user.user_details.FIRST_NAME",
  MIDDLE_NAME:              "ui.user_management.create_user.user_details.MIDDLE_NAME",
  LAST_NAME:                "ui.user_management.create_user.user_details.LAST_NAME",
  EMAIL:                    "ui.common.fields.EMAIL",
  USER_STATUS:              "ui.common.fields.USER_STATUS",
  PARENT_CUSTOMER:          "ui.common.fields.PARENT_CUSTOMER",
  MANDATORY_SOFTWARE_PACKAGES:
    "ui.user_management.create_user.user_details.MANDATORY_SOFTWARE_PACKAGES",
  PRODUCT_LINE:             "ui.common.fields.PRODUCT_LINE",
  PRODUCT_MODALITY:         "ui.common.fields.PRODUCT_MODALITY",
  PRODUCT_SUB_ASSEMBLY:     "ui.common.fields.PRODUCT_SUB_ASSEMBLY",
  SYSTEM_SERIAL_NUMBER:     "ui.common.fields.SYSTEM_SERIAL_NUMBER",
  SRU_HEAD_SERIAL_NUMBER:   "ui.common.fields.SRU_HEAD_SERIAL_NUMBER",
  CRU_AIOC_SERIAL_NUMBER:   "ui.common.fields.CRU_AIOC_SERIAL_NUMBER",
  INJECTOR_ID:              "ui.common.fields.INJECTOR_ID",
  PASSLINK_SYSTEM_ID:       "ui.common.fields.PASSLINK_SYSTEM_ID",
  POSTAL_CODE:              "ui.common.fields.POSTAL_CODE",
  CONTRACT_NAME:            "ui.common.fields.CONTRACT_NAME",
  CONTRACT_EXPIRY_DATE:     "ui.common.fields.CONTRACT_EXPIRY_DATE",
  REMOVE: "ui.common.REMOVE",
  PASSLINK_MOBILE_ACCESS_END_DATE: "ui.common.fields.PASSLINK_MOBILE_ACCESS_END_DATE",
  CUSTOMER_ACCOUNT_NUMBER: "ui.common.fields.CUSTOMER_ACCOUNT_NUMBER",
  // Validation messages (edit / add-edit user)
  IS_REQUIRED: "ui.common.field.IS_REQUIRED",
  PASS_REQ_1: "ui.profile.edit.PASS_REQ_1",
  VALID_MSG17: "ui.user_management.add_edit_customer.VALID_MSG17",
  VALID_MSG18: "ui.user_management.add_edit.VALID_MSG18",
  VALID_MSG19: "ui.user_management.add_edit.VALID_MSG19",
  VALID_MSG20: "ui.user_management.add_edit.VALID_MSG20",
  VALID_MSG15: "ui.user_management.add_edit_customer.VALID_MSG15",
  VALID_MSG16: "ui.user_management.add_edit_customer.VALID_MSG16",
  TOOLTIP1: "ui.user_management.add_edit_customer.tooltip1",
  SUCCESS_TITLE: "ui.software_manager.success.SUCCESS_TITLE",
} as const;

/**
 * Transloco translation keys for the Support module.
 * Map 1:1 to the keys returned by the backend translation catalog.
 */
export const SUPPORT_I18N = {
  // Raise ticket / form
  RAISE_TICKET: "ui.support.RAISE_TICKET",
  TYPE_OF_ISSUE: "ui.support.ISSUE_TYP",
  RAISE_ISSUE: "ui.support.raise_ticket.RAISE_ISSUE",
  RAISE_REQUEST: "ui.support.raise_ticket.RAISE_REQ",
  ISSUE_SUB: "ui.support.raise_ticket.ISSUE_SUB",
  PRIORITY: "ui.support.PRIORITY",
  DESCRIPTION: "ui.common.fields.DESCRIPTION",
  ATTACH_FILES: "ui.support.raise_ticket.ATTACH_FILES",
  NO_FILE_CHOSEN: "ui.support.raise_ticket.NO_FILE_CHOSEN",
  DESC_PLACEHOLDER1: "ui.support.view.DESC_PLACEHOLDER1",
  CHAR: "ui.support.view.CHAR",
  DRAG_DROP: "ui.support.raise_ticket.DRAG_DROP",
  BROWSE: "ui.support.raise_ticket.BROWSE",
  FILE_VALID_MSG1: "ui.support.attachment.FILE_VALID_MSG1",
  FILE_VALID_MSG2: "ui.support.attachment.FILE_VALID_MSG2",
  FILE_VALID_MSG3: "ui.support.attachment.FILE_VALID_MSG3",
  FILE_VALID_MSG5: "ui.support.attachment.FILE_VALID_MSG5",
  SUBMIT: "ui.common.actions.SUBMIT",
  CANCEL: "ui.common.actions.CANCEL",
  SELECT: "ui.common.actions.SELECT",
  TICKET_CREATED: "ui.support.raise_ticket.TICKET_CRCEATED",
  TICKET_NUMBER: "ui.support.raise_ticket.TICKET_NO",
  TICKET_SUCCESS_MSG: "ui.support.raise_ticket.TICKET_SUCCESS_MSG",
  SUB_PLACEHOLDER: "ui.support.view.SUB_PLACHOLDER",
  VALID_MSG1: "ui.support.create.VALID_MSG1",
  VALID_MSG2: "ui.support.create.VALID_MSG2",
  // View ticket
  BACK_TICKET_LIST: "ui.support.view.BACK_TICKET_LIST",
  REQ: "ui.support.view.REQ",
  ON_BEHALF: "ui.support.ON_BEHALF",
  ATTACHMENTS: "ui.support.view.ATTACHMENTS",
  SHARE_RESPONSE: "ui.support.view.SHARE_RESPONSE",
  DESC_PLACEHOLDER: "ui.support.view.DESC_PLACEHOLDER",
  REOPEN_TICKET: "ui.support.REOPEN_TICKET",
  CLOSE_TICKET: "ui.support.CLOSE_TICKET",
  DONE: "ui.common.actions.DONE",
  CREATED_BY: "ui.support.CREATED_BY",
  ISSUE: "ui.support.ISSUE",
  CRITICAL: "ui.support.CRITICAL",
  HIGH: "ui.support.HIGH",
  MEDIUM: "ui.support.MEDIUM",
  LOW: "ui.support.LOW",
  CLOSED: "ui.support.CLOSED",
  NEW: "ui.support.NEW",
  ACTIVE:"ui.common.status.ACTIVE",
  RESOLVED: "ui.support.RESOLVED",
  REMOVED: "ui.support.REMOVED",
  // Table columns / listing
  TICKET_NO: "ui.support.TICKET_NO",
  SUBJECT: "ui.support.SUB",
  DESC: "ui.common.fields.DESCRIPTION",
  REQ_NAME: "ui.support.REQ_NAME",
  STATUS: "ui.common.fields.STATUS",
  CREATED_DATE: "ui.support.CREATED_DATE",
  ACTIONS: "ui.common.fields.ACTION",
  VIEW: "ui.common.actions.VIEW",
  // Shared table / filter chrome
  SHOWING_ENTRIES: "ui.analytics.common.SHOWING_ENTRIES",
  SEARCH: "ui.common.actions.SEARCH",
  SELECT_DATE_RANGE: "ui.common.placeholders.SELECT_DATE_RANGE",
  APPLY: "ui.common.actions.APPLY",
  RESET: "ui.common.actions.RESET",
  FILTER: "ui.analytics.common.FILTER",
} as const;

export const SESSION_CONSTANTS = {
  ACCESS_TOKEN: "access_token",
  ACCESS_TOKEN_CAMEL: "accessToken",
  TOKEN: "token",
  AUTH_TOKEN: "authToken",
  JWT: "jwt",
  CSRF_TOKEN: "_csrf_token",
  PROFILE: "user_profile",
  USER_REGION: "user_region",
  IS_LOGGED_IN: "is_logged_in",
  IS_AUTHORIZED: "is_authorized",
  CUSTOMER_DETAILS: "customer_details",
  SELECTED_INJECTORS: "selected_injectors",
  USER_DETAILS: "user_details",
  USER_INJECTOR_DATA_ACCESS: "user_injector_data_access",
  EXPIRES_IN: "expires_in",
  DECODED_TOKEN_DATA: "decoded_token_data",
  CHECK_INTERVAL_IN_SECONDS: 60,
  SESSION_RENEW_SECONDS: 300,
  SHOW_TIMER_AT: 15,
  IS_TERMS_AND_CONDITIONS_ACCEPTED: "isTermsAndConditionsAccepted",
  DEFAULT_ASSIGNED_REGION: "default_assigned_region",
  USER_ENTITLEMENT_SOFTWARE_PACKAGE: "user_entitlement_software_package",
  USER_MANDATORY_SOFTWARE_PACKAGE: "user_mandatory_software_package",
  SOFTWARE_PACKAGE_CACHE_TIME: "software_package_cache_time",
  TOKEN_COOKIE_NAMES: [
    "access_token",
    "accessToken",
    "token",
    "authToken",
    "jwt",
  ],
  USER_PROFILE_LOWER_DATA: "USER_PROFILE_LOWER_DATA",
  X_REGION: "x-region",
  AUDIT_LOG_REGION: "audit_log_region", // Audit log selected region
  CUSTOMER_ONBOARDING_REGION: "customer-onboarding-region",
  ANALYTICS_REGION: "analytics_region", // Analytics selected region
};

export const SOFTWARE_CACHE_KEYS = {
  ENTITLEMENT: SESSION_CONSTANTS.USER_ENTITLEMENT_SOFTWARE_PACKAGE,
  MANDATORY: SESSION_CONSTANTS.USER_MANDATORY_SOFTWARE_PACKAGE,
  TIMESTAMP: SESSION_CONSTANTS.SOFTWARE_PACKAGE_CACHE_TIME,
  CACHE_DURATION: 5 * 60 * 1000, // 5 minutes
};

export const MESSAGE_CONSTANTS = {
  SESSION_EXPIRED: {
    severity: "warn",
    summary: "Session Expired",
    detail: "You have been logged out.",
    life: 4000,
  },
};

export const RESTRICTED_EMAIL_DOMAINS = [
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "yahoo.co.in",
  "aol.com",
  "abc.com",
  "xyz.com",
  "pqr.com",
  "rediffmail.com",
  "live.com",
  "outlook.com",
  "me.com",
  "msn.com",
  "ymail.com",
  "dispostable.com",
  "yopmail.com",
];

//TBD : should come from backend in future
export const USER_ROLES: Role[] = [
  Role.SUPER_ADMIN,
  Role.REGIONAL_ADMIN,
  Role.AUTHORIZED_DEALER_DISTRIBUTOR,
  Role.FIELD_SERVICE_ENGINEER,
  Role.CUSTOMER_EXTERNAL_ADMIN,
  Role.CYBER_AUTHORIZED_PERSONAL,
  Role.BIO_MEDICAL_ENGINEER,
  Role.GLOBAL_SERVICE_ENGINEER,
];

export const HOME_MENU: Menu = {
  title: "Home",
  i18nKey: "ui.common.fields.HOME",
  index: 0,
  route: "/home",
  roles: [
    Role.SUPER_ADMIN,
    Role.REGIONAL_ADMIN,
    Role.AUTHORIZED_DEALER_DISTRIBUTOR,
    Role.FIELD_SERVICE_ENGINEER,
    Role.CUSTOMER_EXTERNAL_ADMIN,
    Role.CYBER_AUTHORIZED_PERSONAL,
    Role.BIO_MEDICAL_ENGINEER,
    Role.GLOBAL_SERVICE_ENGINEER,
  ],
  permissions: [Permission.USER_ANALYTICS],
  subMenus: [
    {
      title: "Overview",
      i18nKey: "ui.analytics.OVERVIEW",
      index: 0,
      roles: [
        Role.SUPER_ADMIN,
        Role.REGIONAL_ADMIN,
        // Role.FIELD_SERVICE_ENGINEER,
      ],
      permissions: [],
      compareBoth: true,
    },
    {
      title: "Software Analytics",
      i18nKey: "ui.analytics.SW_ANALYTICS",
      index: 1,
      roles: [
        Role.SUPER_ADMIN,
        Role.REGIONAL_ADMIN,
        Role.GLOBAL_SERVICE_ENGINEER,
        Role.CUSTOMER_EXTERNAL_ADMIN,
        Role.BIO_MEDICAL_ENGINEER,
        Role.AUTHORIZED_DEALER_DISTRIBUTOR,
        Role.CYBER_AUTHORIZED_PERSONAL,
        Role.FIELD_SERVICE_ENGINEER,
      ],
      permissions: [],
      compareBoth: true,
    },
    {
      title: "User Analytics",
      i18nKey: "ui.analytics.USR_ANALYTICS",
      index: 2,
      roles: [
        Role.SUPER_ADMIN,
        Role.REGIONAL_ADMIN,
        Role.CUSTOMER_EXTERNAL_ADMIN,
      ],
      permissions: [],
      compareBoth: true,
    },
    {
      title: "Customer Analytics",
      i18nKey: "ui.analytics.CSTMR_ANALYTICS",
      index: 3,
      roles: [Role.SUPER_ADMIN, Role.REGIONAL_ADMIN],
      permissions: [],
      compareBoth: true,
    },
    {
      title: "Passlink Mobile",
      i18nKey: "ui.audit_log.module.PASSLINK_MOBILE",
      index: 4,
      roles: [Role.SUPER_ADMIN],
      permissions: [],
      compareBoth: true,
    },
    {
      title: "VirtualCare",
      i18nKey: "ui.analytics.VIRTUAL_CARE",
      index: 5,
      roles: [Role.SUPER_ADMIN, Role.REGIONAL_ADMIN],
      permissions: [],
      compareBoth: true,
    },
    {
      title: "Injector Data Analytics",
      i18nKey: "ui.analytics.INJ_DATA_ANALYTICS",
      index: 6,
      roles: [Role.CYBER_AUTHORIZED_PERSONAL],
      permissions: [],
      compareBoth: true,
    },
  ] as SubMenu[],
};

export const USER_MANAGEMENT_MENU: Menu = {
  title: "User Management",
  i18nKey: "ui.common.modules.USER_MANAGEMENT",
  index: 1,
  route: "/user-management",
  roles: [Role.SUPER_ADMIN],
  permissions: [
    Permission.EDIT_UPDATE_DEACTIVATE_CYBER_USER,
    Permission.ONBOARD_ACTIVATE_DEACTIVATE_USERS_EDIT,
    Permission.OTHER_EXTERNAL_USER_ONBOARDING_WITHIN_SAME_CUSTOMER_SITE,
    Permission.PERMISSION_CONFIGURATION,
    Permission.SETUP_MANAGE_TAC_ADMIN_USERS_GLOBAL_SERVICE_TEAM,
  ],
  subMenus: [
    {
      title: "Users",
      index: 0,
      roles: [
        Role.SUPER_ADMIN,
        Role.REGIONAL_ADMIN,
        Role.CUSTOMER_EXTERNAL_ADMIN,
      ],
      permissions: [],
      compareBoth: false,
    },
    {
      title: "Draft",
      index: 1,
      roles: [
        Role.SUPER_ADMIN,
        Role.REGIONAL_ADMIN,
        Role.CUSTOMER_EXTERNAL_ADMIN,
      ],
      permissions: [],
      compareBoth: false,
    },
    {
      title: "Permission Settings",
      index: 3,
      roles: [Role.SUPER_ADMIN],
      permissions: [],
      compareBoth: false,
    },
  ] as SubMenu[],
};

export const SOFTWARE_MANAGER_MENU: Menu = {
  title: "Software Manager",
  i18nKey: "ui.common.modules.SOFTWARE_MANAGER",
  index: 2,
  route: "/software-manager",
  roles: [
    Role.SUPER_ADMIN,
    Role.REGIONAL_ADMIN,
    Role.GLOBAL_SERVICE_ENGINEER,
    Role.CUSTOMER_EXTERNAL_ADMIN,
    Role.CYBER_AUTHORIZED_PERSONAL,
    Role.BIO_MEDICAL_ENGINEER,
    Role.AUTHORIZED_DEALER_DISTRIBUTOR,
    Role.FIELD_SERVICE_ENGINEER,
  ],
  permissions: [
    Permission.VIEW_DOWNLOAD_MANDATORY_SW_PACKAGES,
    Permission.VIEW_DOWNLOAD_ENTITLEMENT_SW_PACKAGES,
    Permission.ACTIVATE_DEACTIVATE_SOFTWARE_PACKAGES,
    Permission.SOFTWARE_PACKAGE_DISTRIBUTION,
  ],
  subMenus: [
    {
      title: "Approve & Publish",
      index: 0,
      roles: [],
      permissions: [
        Permission.ACTIVATE_DEACTIVATE_SOFTWARE_PACKAGES,
        Permission.SOFTWARE_PACKAGE_DISTRIBUTION,
      ],
      compareBoth: false,
    },
    {
      title: "Software Packages",
      index: 1,
      roles: [],
      permissions: [
        Permission.VIEW_DOWNLOAD_MANDATORY_SW_PACKAGES,
        Permission.VIEW_DOWNLOAD_ENTITLEMENT_SW_PACKAGES,
        Permission.PRODUCT_LINE_ONBOARDING,
        Permission.USER_ANALYTICS,
        Permission.CUSTOMER_ONBOARDING,
        Permission.PERMISSION_CONFIGURATION,
        Permission.INJECTOR_AUTHENTICATION,
        Permission.ACTIVATE_DEACTIVATE_SOFTWARE_PACKAGES,
        Permission.EDIT_UPDATE_DEACTIVATE_CYBER_USER,
        Permission.ONBOARD_ACTIVATE_DEACTIVATE_USERS_EDIT,
        Permission.OTHER_EXTERNAL_USER_ONBOARDING_WITHIN_SAME_CUSTOMER_SITE,
        Permission.SOFTWARE_PACKAGE_DISTRIBUTION,
      ],
      compareBoth: false,
    },
    {
      title: "Deactivated Packages",
      index: 2,
      roles: [Role.SUPER_ADMIN, Role.REGIONAL_ADMIN],
      permissions: [
        Permission.ACTIVATE_DEACTIVATE_SOFTWARE_PACKAGES,
        Permission.SOFTWARE_PACKAGE_DISTRIBUTION,
      ],
      compareBoth: false,
    },
  ] as SubMenu[],
};

export const CUSTOMER_ONBOARDING_MENU: Menu = {
  title: "Customer Onboarding",
  i18nKey: "ui.common.modules.CUSTOMER_ONBOARDING",
  index: 3,
  route: "/customer-onboarding",
  roles: [Role.SUPER_ADMIN, Role.REGIONAL_ADMIN],
  permissions: [Permission.CUSTOMER_ONBOARDING],
  subMenus: [
    {
      title: "Customers",
      index: 0,
      roles: [Role.SUPER_ADMIN, Role.REGIONAL_ADMIN],
      permissions: [],
      compareBoth: false,
    },
  ] as SubMenu[],
};

export const PRODUCT_ONBOARDING_MENU: Menu = {
  title: "Product Onboarding",
  index: 4,
  route: "/product-onboarding",
  roles: [Role.SUPER_ADMIN],
  permissions: [Permission.PRODUCT_LINE_ONBOARDING],
  subMenus: [
    {
      title: "All Product Lines",
      index: 0,
      roles: [Role.SUPER_ADMIN],
      permissions: [],
      compareBoth: false,
    },
  ] as SubMenu[],
};

export const INJECTOR_DATA: Menu = {
  title: "Injector Data",
  i18nKey: "ui.audit_log.module.INJECTOR_DATA",
  index: 4,
  route: "/injector-data",
  roles: [Role.SUPER_ADMIN],
  permissions: [Permission.INJECTOR_AUTHENTICATION],
  subMenus: [
    {
      title: "All Product Lines",
      index: 0,
      roles: [Role.SUPER_ADMIN, Role.CYBER_AUTHORIZED_PERSONAL],
      permissions: [],
      compareBoth: true,
    },
  ] as SubMenu[],
};

export const AUDIT_LOG_MENU: Menu = {
  title: "Audit Logs",
  i18nKey: "ui.audit_log.MODULE_TITLE",
  index: 5,
  route: "/audit-log",
  permissions: [],
  roles: USER_ROLES,
  subMenus: [] as SubMenu[],
};

export const BIOMED_CONFIG_MENU: Menu = {
  title: BIOMED_CONFIGURATION_CONSTANTS.BIOMED_CONFIGURATION_PAGE_TITLE,
  index: 5,
  route: "/configuration",
  permissions: [],
  roles: [Role.SUPER_ADMIN, Role.GLOBAL_SERVICE_ENGINEER],
  subMenus: [
    {
      title:
        BIOMED_CONFIGURATION_CONSTANTS.BIOMED_CONFIGURATION_SUBMENU_TITLE[0],
      index: 0,
      roles: [Role.SUPER_ADMIN],
      permissions: [],
      compareBoth: true,
    },
    {
      title:
        GLOBAN_CONFIGURATION_CONSTANTS.GLOBAN_CONFIGURATION_SUBMENU_TITLE[0],
      index: 1,
      roles: [Role.SUPER_ADMIN, Role.GLOBAL_SERVICE_ENGINEER],
      permissions: [],
      compareBoth: true,
    },
  ] as SubMenu[],
};

export const MENU_LIST: Menu[] = [
  HOME_MENU,
  USER_MANAGEMENT_MENU,
  SOFTWARE_MANAGER_MENU,
  CUSTOMER_ONBOARDING_MENU,
  PRODUCT_ONBOARDING_MENU,
  AUDIT_LOG_MENU,
  BIOMED_CONFIG_MENU,
  // INJECTOR_DATA
];

/* Moving non-environment specific constants */
export const API_REGION_PREFIX_MAPPING = {
  "US": "us_api",
  "LATAM": "latam_api",
  "EMEA": "emea_api",
  "APAC": "apac_api",
};
/**Support-channel contact details used by support/escalation flows. */
export const SUPPORT_CONTACTS = {
  DEFAULT_EMAIL: "TAC@bayer.com",
  US_PHONE: "+1-800-633-7237",
  EU_CHAT_LINK: "https://EUTAC.Net",
  REGION_EMAILS: {
    "US": "TAC@bayer.com",
    "USA": "TAC@bayer.com",
    "AMERICAS": "TAC@bayer.com",
    "EU": "EUTAC@bayer.com",
    "EMEA": "EUTAC@bayer.com",
    "APAC": "apstac@bayer.com",
    "ASIA PACIFIC": "apstac@bayer.com",
    "FALLBACK": "passlinksupport@bayer.com",
  },
  LATAM_COUNTRIES: [
    { country: "CONOSUR", email: "st_rad_conosur@bayer.com" },
    { country: "PACA", email: "service-paca-rad@bayer.com" },
    { country: "Mexico", email: "servicio.comodato@bayer.com" },
    { country: "Brazil", email: "tac_brasil@bayer.com" },
  ],
};

/**
 * Get dynamic menu list based on user's software package access and role
 * @param userEntitlementSoftwarePackage - User has entitlement software package access
 * @param userMandatorySoftwarePackage - User has mandatory software package access
 * @param userRole - User's role object with id and name
 * @returns Menu array with conditional SOFTWARE_MANAGER_MENU inclusion
 */
export function getDynamicMenuList(
  userEntitlementSoftwarePackage?: boolean,
  userMandatorySoftwarePackage?: boolean,
  userRole?: { id: number; name: string },
): Menu[] {
  const baseMenus = [...MENU_LIST];

  // Check if user is BioMedical Engineer (role ID 7 or role name "BioMedical Engineer")
  const isBioMedicalEngineer =
    userRole?.id === Role.BIO_MEDICAL_ENGINEER || userRole?.name === Roles[7];

  // Check if user is Cyber Authorized Personnel (role ID 6 or role name "Cyber Authorized personnel")
  const isCyberAuthorizedPersonnel =
    userRole?.id === Role.CYBER_AUTHORIZED_PERSONAL ||
    userRole?.name === Roles[6];

  // Remove SOFTWARE_MANAGER_MENU if user doesn't have software package access AND is a BioMedical Engineer or Cyber Authorized Personnel
  // Keep SOFTWARE_MANAGER_MENU if user has any software package access OR is not one of these special roles
  if (
    !userEntitlementSoftwarePackage &&
    !userMandatorySoftwarePackage &&
    (isBioMedicalEngineer || isCyberAuthorizedPersonnel)
  ) {
    baseMenus.splice(baseMenus.indexOf(SOFTWARE_MANAGER_MENU), 1);
  }

  const auditIndex = baseMenus.findIndex(
    (m) => m.route === AUDIT_LOG_MENU.route,
  );
  if (auditIndex !== -1) {
    baseMenus[auditIndex] = {
      ...baseMenus[auditIndex],
      title: "Audit Logs",
    } as Menu;
  }

  return baseMenus;
}

export const PRIORITY_OPTIONS = [
  { label: "Critical", value: 4 },
  { label: "High", value: 3 },
  { label: "Medium", value: 2 },
  { label: "Low", value: 1 },
];

/**
 * HTTP Interceptor Configuration
 * Define which endpoints should be excluded from automatic credential injection
 * and how region-based routing should be handled
 */
export const HTTP_INTERCEPTOR_CONFIG = {
  CREDENTIALS_EXCLUDED_ENDPOINTS: [
    configuration.authUrls.login,
    configuration.authUrls.resetPassword,
    configuration.authUrls.signUp,
  ],
  X_REGION_HEADER_ADDITION: [
    configuration.partialUrls.userManagement.getUsers,
    configuration.partialUrls.userManagement.userDetailsFromToken,
    "search/user-management", // Direct path to handle URL prefixes
  ],
  AUDIT_LOG_REGION_HEADER_ADDITION: [
    "event-logs-frontend", // Audit logs endpoints
    "event-logs",
  ],
  CUSTOMER_ONBOARDING_REGION_HEADER_ADDITION: [
    "customer-onboarding",
    "search/customer-onboarding",
    "injectors",
    "search/injectors",
  ],
  ANALYTICS_REGION_HEADER_ADDITION: [
    "analytics",
    "software-analytics",
    "user-analytics",
    "customer-analytics",
    "injector-data-analytics",
  ],
  URL_REGION_MODIFICATION: [
    "user-management",
    "customer-onboarding",
    "injectors",
    "reference",
    "auth",
    "event-logs-frontend",
    "event-logs",
    "analytics",
    "software-analytics",
    "user-analytics",
    "customer-analytics",
    "injector-data-analytics",
  ],
  URL_REGION_MODIFICATION_EXCLUDED: [
    configuration.partialUrls.userManagement.userDetailsFromToken, // "/user-management/me"
    // "user-management/accept-terms-and-condition", // Accept terms and condition endpoint
    // Add more endpoints here that should use X-Region header instead of URL modification
  ],
};

export const AUDIT_LOG_MODULES = [
  {
    label: "Software Manager",
    i18nKey: "ui.common.modules.SOFTWARE_MANAGER",
    value: "software-manager",
    key: "SOFTWARE",
    roles: [Role.SUPER_ADMIN, Role.GLOBAL_SERVICE_ENGINEER],
  },
  {
    label: "User Management",
    i18nKey: "ui.common.modules.USER_MANAGEMENT",
    value: "user-management",
    key: "USER",
    roles: [
      Role.SUPER_ADMIN,
      Role.REGIONAL_ADMIN,
      Role.CUSTOMER_EXTERNAL_ADMIN,
    ],
  },
  {
    label: "Customer Onboarding",
    i18nKey: "ui.common.modules.CUSTOMER_ONBOARDING",
    value: "customer-onboarding",
    key: "CUSTOMER",
    roles: [Role.SUPER_ADMIN, Role.REGIONAL_ADMIN],
  },
  {
    label: "Product Onboarding",
    i18nKey: "ui.common.modules.PRODUCT_ONBOARDING",
    value: "product-onboarding",
    key: "PRODUCT",
    roles: [Role.SUPER_ADMIN],
  },
  {
    label: "Passlink Mobile",
    i18nKey: "ui.audit_log.module.PASSLINK_MOBILE",
    value: "mobile",
    key: "MOBILE",
    roles: [
      Role.SUPER_ADMIN,
      Role.REGIONAL_ADMIN,
      Role.AUTHORIZED_DEALER_DISTRIBUTOR,
      Role.FIELD_SERVICE_ENGINEER,
      Role.CUSTOMER_EXTERNAL_ADMIN,
      Role.CYBER_AUTHORIZED_PERSONAL,
      Role.BIO_MEDICAL_ENGINEER,
      Role.GLOBAL_SERVICE_ENGINEER,
    ],
  },
  {
    label: "User Activity",
    i18nKey: "ui.audit_log.module.USER_ACTIVITY",
    value: "cloud",
    key: "CLOUD",
    roles: [
      Role.SUPER_ADMIN,
      Role.REGIONAL_ADMIN,
      Role.AUTHORIZED_DEALER_DISTRIBUTOR,
      Role.FIELD_SERVICE_ENGINEER,
      Role.CUSTOMER_EXTERNAL_ADMIN,
      Role.CYBER_AUTHORIZED_PERSONAL,
      Role.BIO_MEDICAL_ENGINEER,
      Role.GLOBAL_SERVICE_ENGINEER,
    ],
  },
  {
    label: "VirtualCare",
    i18nKey: "ui.audit_log.module.VIRTUAL_CARE",
    value: "virtual-care",
    key: "VIRTUAL_CARE",
    roles: [Role.SUPER_ADMIN, Role.GLOBAL_SERVICE_ENGINEER],
  },
  {
    label: "Injector Data",
    i18nKey: "ui.audit_log.module.INJECTOR_DATA",
    value: "injector-data",
    key: "INJECTOR_DATA",
    roles: [
      Role.SUPER_ADMIN,
      Role.REGIONAL_ADMIN,
      Role.CYBER_AUTHORIZED_PERSONAL,
    ],
  },
  {
    label: "System Configuration",
    i18nKey: "ui.common.modules.SYSTEM_CONFIGURATION",
    value: "system-configuration",
    key: "SYSTEM_CONFIGURATION",
    roles: [Role.SUPER_ADMIN, Role.GLOBAL_SERVICE_ENGINEER],
  },
];

export const AUDIT_LOG_CONSTANTS = {
  DATE_FORMAT: COMMON_DATE_FORMAT,
  MAX_EXPORT_DAYS: 90, // Maximum days allowed for export date range
  SEARCH_PLACEHOLDER: "Search by username...",
  SEARCH_PLACEHOLDER_I18N: "ui.analytics.common.SEARCH_BY_USERNAME",
  EXPORT_LABEL: "Export",
  EXPORT_LABEL_I18N: "ui.common.actions.EXPORT",
};

export const TABLE_SCHEMA = {
  SOFTWARE_MANAGER: [
    USERNAME,
    EMAIL_ADDRESS,
    ROLE_TYPE,
    TIMESTAMP,
    ACTION,
    PRODUCT_LINE,
    PRODUCT_SOFTWARE_VERSION,
    ADD_ON_FEATURE,
    REMARKS
  ],
  USER_MANAGEMENT: [
    USERNAME,
    ROLE_TYPE,
    TIMESTAMP,
    ACTION,
    NEW_USER_NAME,
    NEW_USER_ROLE,
    NEW_USER_ASSIGNED_REGIONS,
    NEW_USER_ASSIGNED_COUNTRIES,
  ],
  CUSTOMER_ONBOARDING: [
    USERNAME,
    EMAIL_ADDRESS,
    ROLE_TYPE,
    TIMESTAMP,
    ACTION,
    CUSTOMER_NAME,
    CUSTOMER_LOCATION,
  ],
  PRODUCT_ONBOARDING: [
    USERNAME,
    EMAIL_ADDRESS,
    ROLE_TYPE,
    TIMESTAMP,
    ACTION,
    PRODUCT_ADDED_EDITED,
  ],
  PASSLINK_MOBILE: [
    USERNAME,
    EMAIL_ADDRESS,
    ROLE_TYPE,
    TIMESTAMP,
    INJECTOR_PLACEMENT_SITE,
    ACTION,
    ACTIVITY_DETAILS,
  ],
  PASSLINK_CLOUD: [
    USERNAME,
    EMAIL_ADDRESS,
    ROLE_TYPE,
    TIMESTAMP,
    ACTION,
    CUSTOMER_NAME,
    DETAILS,
  ],
  VIRTUAL_CARE: [
    ACTION,
    SOFTWARE_PACKAGE,
    DATA_SENT_OR_RECEIVED,
    PRODUCT_LINE,
    PRODUCT_SOFTWARE_VERSION,
    TIMESTAMP,
  ],
  INJECTOR_DATA: [
    USERNAME,
    REQUEST_ID,
    FILE_NAME,
    FILE_SIZE,
    UPDATED_AT,
    PRODUCT_LINE,
    NO_OF_INJECTORS,
    CUSTOMER_NAME,
    STATUS,
  ],
  SYSTEM_CONFIGURATION: [
    USERNAME,
    EMAIL_ADDRESS,
    ROLE_TYPE,
    CREATED_AT,
    ACTION,
    ENTITY,
    REMARKS,
  ],
};

export const TABLE_FILTERS = {
  SOFTWARE_MANAGER: [
    PRODUCT_NAME_FILTER,
    USER_ROLE_FILTER,
    ACTION_FILTER,
    CREATED_DATE_FILTER,
  ],
  USER_MANAGEMENT: [
    USER_ROLE_FILTER,
    ACTION_FILTER,
    COUNTRY_FILTER,
    CREATED_DATE_FILTER,
  ],
  CUSTOMER_ONBOARDING: [
    USER_ROLE_FILTER,
    ACTION_FILTER,
    COUNTRY_FILTER,
    CREATED_DATE_FILTER,
  ],
  PRODUCT_ONBOARDING: [
    PRODUCT_NAME_FILTER,
    USER_ROLE_FILTER,
    ACTION_FILTER,
    CREATED_DATE_FILTER,
  ],
  PASSLINK_MOBILE: [
    USER_ROLE_FILTER,
    ACTION_FILTER,
    COUNTRY_FILTER,
    CREATED_DATE_FILTER,
  ],
  PASSLINK_CLOUD: [
    USER_ROLE_FILTER,
    ACTION_FILTER,
    COUNTRY_FILTER,
    CREATED_DATE_FILTER,
  ],
  VIRTUAL_CARE: [ACTION_FILTER, CREATED_DATE_FILTER],
  INJECTOR_DATA: [PRODUCT_NAME_FILTER, STATUS_FILTER, CREATED_DATE_FILTER],
  SYSTEM_CONFIGURATION: [ACTION_FILTER, CREATED_DATE_FILTER],
};

export const DASHBOARD_SIZE_CONSTANTS = {
  CARDS_2: "392px",
  CARDS_3: "596px",
};

export const NOTIFICATION_CONSTANTS = {
  ALL: "ui.notification.ALL",
  UNREAD: "ui.notification.UNREAD",
  READ: "ui.notification.READ",
  /** Tab label i18n keys (display). Order matches `filters`. */
  types: [
    "ui.notification.ALL",
    "ui.notification.UNREAD",
    "ui.notification.READ",
  ] as const,
  /** API / pageInfo filter values. Order matches `types`. */
  filters: ["all", "unread", "read"] as const,
  typeFallbacks: ["All", "Unread", "Read"] as const,
};

// ==================================================
// FILE UPLOAD / DOCUMENT HANDLING CONFIGURATION
// ==================================================
// When validating or storing files uploaded by users we treat
// every piece of input as untrusted.  The allowed extensions
// list is intentionally narrow, filenames are sanitized and
// normalized to include exactly one extension, and the same
// rules that run in the browser **must** also be enforced on
// the server to avoid CWE-602 (insecure client-side
// validation only).
export const FILE_UPLOAD_CONFIG = {
  // Only files ending with one of these extensions will be
  // accepted.  Do **not** rely on MIME type or the browser's
  // guess; always re-check on the backend.
  ALLOWED_EXTENSIONS: [
    ".png",
    ".jpeg",
    ".jpg",
    ".pdf",
    ".xlsx",
    ".txt",
  ] as const,

  // maximum size allowed for uploads in bytes (1MB)
  MAX_SIZE_BYTES: 1 * 1024 * 1024,
};

export const ICON_COLORS = [
  "#FF69B4",
  "#FFD93D",
  "#2EC4B6",
  "#FF6F61",
  "#CDB4DB",
];
